import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiUrlGenerator } from 'infrastructure/hooks/use-api';
import { showResult } from 'infrastructure/hooks/utils';
import dataService from 'services/dataService';
import {
  apiAdminMedications,
  apiPapFormTemplateDownload,
  apiLetterTemplates,
  apiPapCaseAttachmentByGuid,
  apiPapCaseAttachmentDownload,
  apiPapCaseByGuid,
  apiPapFormDownload,
  apiPapCaseForms,
  apiPapCaseFormsConfirm,
  apiPapCaseLinkByGuid,
  apiPapCaseLinks,
  apiPapCaseReviewByGuid,
  apiPatientPapCases,
  createAttachmentPresignedUrl,
  uploadAttachment,
} from 'services/dataService/resources';

import type {
  AttachmentPresignedUrlResponse,
  DeleteAttachmentPayload,
  IConfirmPapCaseFormApiResponse,
  ICreatePapCaseExternalLinkPayload,
  ICreatePapCaseFormApiResponse,
  ICreatePapCasePayload,
  IDeletePapCaseExternalLinkPayload,
  ILetterTemplateApiItem,
  ILetterTemplateApiResponse,
  IMedicationApiResponse,
  IPapCaseExternalResource,
  IPapCaseLinkApiItem,
  IPapCasesApiResponse,
  IPapFormDownloadApiResponse,
  IPapFormMedication,
  IPapLetterTemplate,
  ISavePapCaseFormPayload,
  ISavePapCaseFormResult,
  IUpdatePapCaseDetailPayload,
  IUpdatePapCaseLetterPayload,
  IUpdatePapCaseNurseNotePayload,
  IUsePapCasesOptions,
  IUsePapCasesResult,
  PapCase,
  PapCaseStatus,
  UploadAttachmentPayload,
} from '../types';

const getPatientPapCasesFn = async (
  patientGuid: string,
  options: IUsePapCasesOptions,
): Promise<IUsePapCasesResult> => {
  const { page = 1, itemsPerPage = 5, sortBy, sortOrder } = options;

  const { error, data } = await dataService.getList<
    IPapCasesApiResponse | PapCase[]
  >(
    apiUrlGenerator(apiPatientPapCases(patientGuid), {
      page,
      items: itemsPerPage,
      sortBy,
      sortOrder,
    }),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  const list = Array.isArray(data) ? data : data?.items || [];
  const total = Array.isArray(data) ? list.length : data?.count || list.length;
  const currentPage = Array.isArray(data) ? page : data?.page || page;
  const currentItemsPerPage = Array.isArray(data)
    ? itemsPerPage
    : data?.itemsPerPage || itemsPerPage;

  return {
    papCases: list,
    count: total,
    page: currentPage,
    itemsPerPage: currentItemsPerPage,
  };
};

const mapPapCaseExternalResource = (
  item: IPapCaseLinkApiItem,
  index: number,
): IPapCaseExternalResource => ({
  id: item.guid || item.id || `resource-${index}`,
  title: item.title || item.name || 'Resource',
  url: item.url || item.link || '',
  description: item.description ?? null,
});

const getPapCaseExternalLinksFn = async (
  papCaseGuid: string,
): Promise<IPapCaseExternalResource[]> => {
  const { error, data } = await dataService.getList<IPapCaseLinkApiItem[]>(
    apiPapCaseLinks(papCaseGuid),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return (data || []).map(mapPapCaseExternalResource);
};

const createPapCaseExternalLinkFn = async (
  payload: ICreatePapCaseExternalLinkPayload,
): Promise<IPapCaseExternalResource> => {
  const { error, data } = await dataService.createOne<
    IPapCaseLinkApiItem | { item?: IPapCaseLinkApiItem }
  >(apiPapCaseLinks(payload.papCaseGuid), {
    title: payload.title,
    url: payload.url,
    description: payload.description ?? null,
  });

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  const createdLink =
    (data as { item?: IPapCaseLinkApiItem })?.item ||
    (data as IPapCaseLinkApiItem);

  return mapPapCaseExternalResource(
    createdLink || {
      title: payload.title,
      url: payload.url,
      description: payload.description ?? null,
    },
    0,
  );
};

const deletePapCaseExternalLinkFn = async (
  payload: IDeletePapCaseExternalLinkPayload,
) => {
  const { error } = await dataService.deleteObject(
    apiPapCaseLinkByGuid(payload.papCaseGuid, payload.linkGuid),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }
};

const deletePapCaseAttachmentFn = async (payload: DeleteAttachmentPayload) => {
  const { error } = await dataService.deleteObject(
    apiPapCaseAttachmentByGuid(payload.papCaseGuid, payload.attachmentGuid),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }
};

const getLetterTemplatesFn = async (): Promise<IPapLetterTemplate[]> => {
  const { error, data } = await dataService.getList<
    ILetterTemplateApiResponse | ILetterTemplateApiItem[]
  >(apiLetterTemplates);

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  const list = Array.isArray(data) ? data : data?.items || [];

  return list
    .filter((item) => item?.guid && item?.name && item?.status === 'active')
    .map((item) => ({
      guid: item.guid as string,
      name: item.name as string,
      content: item.content || '',
    }));
};

const getPapFormMedicationsFn = async (): Promise<IPapFormMedication[]> => {
  const { error, data } = await dataService.getList<IMedicationApiResponse>(
    apiUrlGenerator(apiAdminMedications(), {
      includeActivePapForm: true,
    }),
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return (data?.items || []).map((item) => ({
    ...item,
    hasActivePapForm: item.activePapForm != null,
    papFormGuid: item.activeMedicationPapFormGuid || '',
  }));
};

export const downloadPapForm = async (
  papFormGuid: string,
  papCaseGuid?: string,
): Promise<string> => {
  if (!papFormGuid) return '';

  const endpoint = papCaseGuid
    ? apiPapFormDownload(papCaseGuid, papFormGuid)
    : apiPapFormTemplateDownload(papFormGuid);

  const { error, data } =
    await dataService.getOnly<IPapFormDownloadApiResponse>(endpoint);

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data?.downloadUrl || '';
};

export const downloadPapCaseAttachment = async (
  papCaseGuid: string,
  attachmentGuid: string,
): Promise<string> => {
  if (!papCaseGuid || !attachmentGuid) return '';

  const { error, data } = await dataService.getOnly<{ downloadUrl: string }>(
    apiPapCaseAttachmentDownload(papCaseGuid, attachmentGuid),
  );

  if (error) {
    showResult(error);
    return '';
  }

  return data?.downloadUrl || '';
};

const savePapCaseMedicationFormFn = async (
  payload: ISavePapCaseFormPayload,
): Promise<ISavePapCaseFormResult> => {
  const fileSize = payload.file.size;
  const mimeType = payload.file.type as 'application/pdf';

  const { error: createError, data: createResponse } =
    await dataService.createOne<ICreatePapCaseFormApiResponse>(
      apiPapCaseForms(payload.papCaseGuid),
      {
        fileName: payload.file.name,
        fileSize,
        mimeType,
        medicationGuid: payload.medicationGuid,
        medicationPapFormGuid: payload.medicationPapFormGuid,
      },
    );

  if (createError) {
    showResult(createError);
    throw new Error(createError);
  }

  const uploadUrl = createResponse?.uploadUrl;
  const formGuid = createResponse?.formGuid;

  if (!uploadUrl || !formGuid) {
    throw new Error('Failed to prepare PAP form upload');
  }

  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
      'x-amz-server-side-encryption': 'AES256',
    },
    body: payload.file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload PAP form PDF');
  }

  const { error: confirmError, data: confirmResponse } =
    await dataService.createOne<IConfirmPapCaseFormApiResponse>(
      apiPapCaseFormsConfirm(payload.papCaseGuid),
      {
        formGuid,
      },
    );

  if (confirmError) {
    showResult(confirmError);
    throw new Error(confirmError);
  }

  return {
    formGuid,
    version: confirmResponse?.version || createResponse?.version,
  };
};

const saveAttachmentFn = async (payload: UploadAttachmentPayload) => {
  const fileSize = payload.file.size;
  const mimeType = payload.file.type;

  const { error: createError, data: createPresignedData } =
    await dataService.createOne<AttachmentPresignedUrlResponse>(
      createAttachmentPresignedUrl(payload.papCaseGuid),
      {
        fileName: payload.file.name,
        fileSize,
        mimeType,
      },
    );

  if (createError) {
    showResult(createError);
    throw new Error(createError);
  }

  const uploadUrl = createPresignedData?.uploadUrl;
  const attachmentGuid = createPresignedData?.attachmentGuid;

  if (!uploadUrl || !attachmentGuid) {
    throw new Error('Failed to prepare PAP form upload');
  }

  const uploadResponse = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': mimeType,
      'x-amz-server-side-encryption': 'AES256',
    },
    body: payload.file,
  });

  if (!uploadResponse.ok) {
    throw new Error('Failed to upload PAP form PDF');
  }

  const { error: confirmError } = await dataService.createOne(
    uploadAttachment(payload.papCaseGuid),
    { attachmentGuid },
  );

  if (confirmError) {
    showResult(confirmError);
    throw new Error(confirmError);
  }
};

export const getPatientPapCasesKey = (
  patientGuid: string,
  options: IUsePapCasesOptions,
) => [
  'patientPapCases',
  patientGuid,
  options.page,
  options.itemsPerPage,
  options.sortBy,
  options.sortOrder,
];

export const getPapLetterTemplatesKey = ['papLetterTemplates'];
export const getPapFormMedicationsKey = ['papFormMedications'];
export const getPapCaseExternalLinksKey = (papCaseGuid?: string) => [
  'papCaseExternalLinks',
  papCaseGuid,
];

export const useGetLetterTemplates = () => {
  return useQuery({
    queryKey: getPapLetterTemplatesKey,
    queryFn: getLetterTemplatesFn,
    staleTime: 60_000,
  });
};

export const useGetPapFormMedications = (enabled = true) => {
  return useQuery({
    queryKey: getPapFormMedicationsKey,
    queryFn: getPapFormMedicationsFn,
    staleTime: 60_000,
    enabled,
  });
};

export const useGetPapCaseExternalLinks = (papCaseGuid?: string) => {
  return useQuery({
    queryKey: getPapCaseExternalLinksKey(papCaseGuid),
    queryFn: async () => getPapCaseExternalLinksFn(papCaseGuid!),
    enabled: !!papCaseGuid,
    staleTime: 10_000,
  });
};

export const useGetPapCaseDetail = (papCaseGuid?: string) => {
  return useQuery({
    queryKey: ['papCaseDetail', papCaseGuid],
    queryFn: async () => {
      const { error, data } = await dataService.getOnly<PapCase>(
        apiPapCaseByGuid(papCaseGuid!),
      );

      if (error) {
        showResult(error);
        throw new Error(error);
      }

      if (!data) {
        throw new Error('PAP case detail is empty');
      }

      return data;
    },
    enabled: !!papCaseGuid,
    staleTime: 10_000,
  });
};

export const useCreatePapCase = (patientGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ICreatePapCasePayload) => {
      const { error, data: response } = await dataService.createOne<
        PapCase | { item?: PapCase }
      >(apiPatientPapCases(patientGuid), payload);

      if (error) {
        showResult(error);
        throw new Error(error);
      }

      const createdCase =
        (response as { item?: PapCase })?.item || (response as PapCase);

      return createdCase;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });
    },
  });
};

export const useUpdatePapCaseLetter = (
  patientGuid: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IUpdatePapCaseLetterPayload) => {
      const { error } = await dataService.patchOnly(
        apiPapCaseByGuid(payload.papCaseGuid),
        {
          content: payload.content,
          letterTemplateGuid: payload.letterTemplateGuid,
        },
      );

      if (error) {
        showResult(error);
        throw new Error(error);
      }
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });

      onSuccess?.();
    },
  });
};

export const useSavePapCaseMedicationForm = (
  patientGuid: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ISavePapCaseFormPayload) => {
      return savePapCaseMedicationFormFn(payload);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });
      onSuccess?.();
    },
  });
};

export const useSavePapCaseAttachment = (
  patientGuid: string,
  onSuccess: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ISavePapCaseFormPayload) => {
      return saveAttachmentFn(payload);
    },
    onSuccess: async (_data, variables) => {
      onSuccess();

      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });
    },
  });
};

export const useUpdatePapCase = (
  patientGuid: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IUpdatePapCaseDetailPayload) => {
      const { error } = await dataService.patchOnly(
        apiPapCaseByGuid(payload.papCaseGuid),
        {
          name: payload.name,
          isSpecialist: payload.isSpecialist,
        },
      );

      if (error) {
        showResult(error);
        throw new Error(error);
      }
    },
    onSuccess: async (_data, variables) => {
      onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });
    },
  });
};

export const useUpdatePapCaseNurseNote = (
  patientGuid: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IUpdatePapCaseNurseNotePayload) => {
      const { error } = await dataService.patchOnly(
        apiPapCaseByGuid(payload.papCaseGuid),
        {
          note: payload.note,
        },
      );

      if (error) {
        showResult(error);
        throw new Error(error);
      }
    },
    onSuccess: async (_data, variables) => {
      onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });
    },
  });
};

export const useCreatePapCaseExternalLink = (
  patientGuid: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: ICreatePapCaseExternalLinkPayload) => {
      return createPapCaseExternalLinkFn(payload);
    },
    onSuccess: async (_data, variables) => {
      onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseExternalLinks', variables.papCaseGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });
    },
  });
};

export const useDeletePapCaseExternalLink = (patientGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: IDeletePapCaseExternalLinkPayload) => {
      await deletePapCaseExternalLinkFn(payload);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: getPapCaseExternalLinksKey(variables.papCaseGuid),
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });
    },
  });
};

export const useDeletePapCaseAttachment = (patientGuid: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: DeleteAttachmentPayload) => {
      await deletePapCaseAttachmentFn(payload);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseAttachments', variables.papCaseGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', variables.papCaseGuid],
      });
    },
  });
};

export const usePapCases = (
  patientGuid: string,
  options: IUsePapCasesOptions = {},
) => {
  const { page = 1, itemsPerPage = 5, sortBy, sortOrder } = options;

  const { data, ...other } = useQuery({
    queryKey: getPatientPapCasesKey(patientGuid, {
      page,
      itemsPerPage,
      sortBy,
      sortOrder,
    }),
    queryFn: async () =>
      getPatientPapCasesFn(patientGuid, {
        page,
        itemsPerPage,
        sortBy,
        sortOrder,
      }),
    enabled: Boolean(patientGuid),
    keepPreviousData: true,
  });

  const downloadPapFormCallback = async (
    papFormGuid: string,
    papCaseGuid?: string,
  ) => {
    return downloadPapForm(papFormGuid, papCaseGuid);
  };

  return {
    papCases: data?.papCases || [],
    count: data?.count || 0,
    page: data?.page || page,
    itemsPerPage: data?.itemsPerPage || itemsPerPage,
    downloadPapForm: downloadPapFormCallback,
    ...other,
  };
};

export const useUpdateStatusPapCase = (
  papCaseGuid: string,
  patientGuid: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ nextStatus }: { nextStatus: PapCaseStatus }) => {
      const { error } = await dataService.patchOnly(
        apiPapCaseByGuid(papCaseGuid),
        {
          status: nextStatus,
        },
      );

      if (error) {
        showResult(error);
        throw new Error(error);
      }
    },
    onSuccess: async () => {
      onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', papCaseGuid],
      });
    },
  });
};

export const useUpdateStatusWithReviewPapCase = (
  papCaseGuid: string,
  patientGuid: string,
  onSuccess?: () => void,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      nextStatus,
      reviewNote,
    }: {
      nextStatus: PapCaseStatus;
      reviewNote: string;
    }) => {
      const { error } = await dataService.createOne(
        apiPapCaseReviewByGuid(papCaseGuid),
        {
          status: nextStatus,
          comment: reviewNote,
        },
      );

      if (error) {
        showResult(error);
        throw new Error(error);
      }
    },
    onSuccess: async () => {
      onSuccess?.();
      await queryClient.invalidateQueries({
        queryKey: ['patientPapCases', patientGuid],
      });

      await queryClient.invalidateQueries({
        queryKey: ['papCaseDetail', papCaseGuid],
      });
    },
  });
};
